/**
 * The shipped default logos — C12, ADR-0030 §3, T-1.27.
 *
 * The files are the owner's PNGs under `apps/web/site-assets/logos/`, and
 * `manifest.json` beside them is the one list of them: file → profession →
 * the name a firm reads under the picture. `scripts/copy-logos.mjs` copies the
 * list into `dist/site-assets/logos/` at build time and **fails the build on an
 * entry with no file**, so a name this screen offers is a picture the server
 * has. They are served first-party at `/site-assets/logos/<file>` on every host
 * — the platform host, a firm's own domain and its portal — the way the fonts
 * are, which is what lets a firm use one with no upload.
 *
 * `other` (ADR-0031) has no default: *"for others they will upload their
 * logos"* (the owner, 2026-09-25). The control says so rather than showing an
 * empty grid.
 */

import manifest from '../../site-assets/logos/manifest.json'

export const DEFAULT_LOGO_PREFIX = '/site-assets/logos/'

export interface DefaultLogo {
  /** The stored value: `/site-assets/logos/<file>`. */
  url: string
  file: string
  profession: string
  name: string
}

export const DEFAULT_LOGOS: DefaultLogo[] = manifest.logos.map((entry) => ({
  url: `${DEFAULT_LOGO_PREFIX}${entry.file}`,
  file: entry.file,
  profession: entry.profession,
  name: entry.name,
}))

/** Every default, the firm's own profession's first (C12 §10.4 item 1), the
 *  rest in the manifest's order. An unknown or empty profession changes
 *  nothing about the order. */
export function defaultLogosFor(profession: string | null | undefined): DefaultLogo[] {
  if (!profession) return DEFAULT_LOGOS
  const own = DEFAULT_LOGOS.filter((logo) => logo.profession === profession)
  return [...own, ...DEFAULT_LOGOS.filter((logo) => logo.profession !== profession)]
}

/** Whether the profession has a default of its own. `false` for `other`. */
export function hasOwnDefault(profession: string | null | undefined): boolean {
  return Boolean(profession) && DEFAULT_LOGOS.some((logo) => logo.profession === profession)
}

/** The default a stored value names, or `null` for an upload or a link. */
export function defaultLogoOf(url: string | null | undefined): DefaultLogo | null {
  if (!url) return null
  return DEFAULT_LOGOS.find((logo) => logo.url === url) ?? null
}

/** Where a stored logo came from, for the line under the preview. */
export function logoSource(url: string | null | undefined): 'none' | 'default' | 'library' | 'link' {
  if (!url) return 'none'
  if (defaultLogoOf(url)) return 'default'
  if (url.startsWith('/site-media/')) return 'library'
  return 'link'
}
